// CoD-style view recoil (DESIGN.md "Accuracy model", INTERNALS.md "Recoil").
//
// The kick is applied to the live view angles themselves, so the aim the next InputCmd reports
// already includes it. Two quantities are tracked:
//
//  - `pending`: kick that has been fired but not yet applied to the view. It is applied with a
//    short exponential approach (reaches ~95 % after 35 ms).
//  - `uncomp`: the part of the kick the player has not countered with the mouse. Counter-moves
//    reduce it (`compensate`); after the fire interval without a new shot the rest returns at the
//    weapon's recovery rate and is moved off the view as it is consumed. Compensation and recovery
//    both draw from the same `uncomp`, so the kick is never recovered twice.
//
// Units: degrees. `pitch` positive = up, `yaw` positive = to the right (as in the weapon pattern).

/** Time constant of the kick approach: 1 - exp(-35 ms / 11.7 ms) = 95 %. */
export const KICK_TAU_SEC = 0.035 / 3;

export interface RecoilStep {
  /** View change to apply this step (degrees, up / right positive). */
  pitch: number;
  yaw: number;
}

function reduceToward(u: number, d: number): number {
  // `d` moves the view against the kick when the signs differ.
  if (u > 0 && d < 0) return Math.max(0, u + d);
  if (u < 0 && d > 0) return Math.min(0, u + d);
  return u;
}

export class Recoil {
  private pendingPitch = 0;
  private pendingYaw = 0;
  private uncompPitch = 0;
  private uncompYaw = 0;
  /** Seconds until recovery may start. */
  private delay = 0;

  /** Uncompensated kick still to be recovered (degrees, pitch up / yaw right). */
  get uncompPitchDeg(): number {
    return this.uncompPitch;
  }

  get uncompYawDeg(): number {
    return this.uncompYaw;
  }

  /** Kick fired but not yet applied to the view (degrees). */
  get pendingPitchDeg(): number {
    return this.pendingPitch;
  }

  /**
   * A predicted shot kicks the view by (pitchDeg up, yawDeg right). Recovery of the whole
   * remainder waits `recoveryDelaySec` after the newest shot (the weapon's `recoveryDelay`: longer
   * than the cycle of an automatic weapon, so a spray keeps its climb, and longer than the pause
   * between two bursts).
   */
  kick(pitchDeg: number, yawDeg: number, recoveryDelaySec: number): void {
    this.pendingPitch += pitchDeg;
    this.pendingYaw += yawDeg;
    this.uncompPitch += pitchDeg;
    this.uncompYaw += yawDeg;
    this.delay = Math.max(0, recoveryDelaySec);
  }

  /**
   * The player moved the view by (pitchDeg up, yawDeg right) with the mouse. Moving against the
   * remaining kick counters it and shrinks what recovery has to return.
   */
  compensate(pitchDeg: number, yawDeg: number): void {
    this.uncompPitch = reduceToward(this.uncompPitch, pitchDeg);
    this.uncompYaw = reduceToward(this.uncompYaw, yawDeg);
  }

  /** Advances `dt` seconds; writes the view change into `out` and returns it. */
  step(dt: number, recoveryDegPerSec: number, out: RecoilStep): RecoilStep {
    let pitch = 0;
    let yaw = 0;

    // 1. Apply pending kick.
    if (this.pendingPitch !== 0 || this.pendingYaw !== 0) {
      const f = 1 - Math.exp(-dt / KICK_TAU_SEC);
      let ap = this.pendingPitch * f;
      let ay = this.pendingYaw * f;
      if (Math.abs(this.pendingPitch - ap) < 1e-4 && Math.abs(this.pendingYaw - ay) < 1e-4) {
        ap = this.pendingPitch;
        ay = this.pendingYaw;
      }
      this.pendingPitch -= ap;
      this.pendingYaw -= ay;
      pitch += ap;
      yaw += ay;
    }

    // 2. Recovery of the uncompensated remainder, once the kick has been applied and the fire
    //    interval has passed without a new shot.
    if (this.delay > 0) {
      this.delay -= dt;
    } else if (
      (this.uncompPitch !== 0 || this.uncompYaw !== 0) &&
      this.pendingPitch === 0 &&
      this.pendingYaw === 0
    ) {
      const mag = Math.hypot(this.uncompPitch, this.uncompYaw);
      const take = Math.min(mag, Math.max(0, recoveryDegPerSec) * dt);
      if (mag <= 1e-5 || take >= mag) {
        pitch -= this.uncompPitch;
        yaw -= this.uncompYaw;
        this.uncompPitch = 0;
        this.uncompYaw = 0;
      } else {
        const k = take / mag;
        const rp = this.uncompPitch * k;
        const ry = this.uncompYaw * k;
        this.uncompPitch -= rp;
        this.uncompYaw -= ry;
        pitch -= rp;
        yaw -= ry;
      }
    }

    out.pitch = pitch;
    out.yaw = yaw;
    return out;
  }

  reset(): void {
    this.pendingPitch = 0;
    this.pendingYaw = 0;
    this.uncompPitch = 0;
    this.uncompYaw = 0;
    this.delay = 0;
  }
}

/** Natural frequency (rad/s) and damping ratio of the camera flinch spring (settles in ~0.15 s). */
const FLINCH_OMEGA = 34;
const FLINCH_ZETA = 0.45;
/** The flinch never tilts the camera further than this (degrees). */
export const FLINCH_MAX_ROLL_DEG = 2.2;

/**
 * Camera roll punch of one shot: a damped spring that is kicked sideways by every shot and rings
 * out in a few frames. It is only drawn (the roll does not change where the bullets go), so the
 * crosshair stays the truth while the camera still jolts. The punch grows with the kick of the
 * shot, so a revolver or a sniper rifle throws the view harder than a submachine gun.
 */
export class Flinch {
  /** Current roll offset (degrees, positive tilts the camera to the right). */
  rollDeg = 0;
  private vel = 0;
  private flip = 1;

  /** A shot with the given view kick (degrees); the camera tilts to alternating sides. */
  kick(pitchDeg: number): void {
    this.flip = -this.flip;
    const strength = Math.min(1.1 + 0.9 * pitchDeg, 6);
    this.vel += this.flip * strength * FLINCH_OMEGA * 0.85;
  }

  step(dt: number): void {
    let left = Math.min(dt, 0.1);
    while (left > 0) {
      const h = Math.min(left, 1 / 120);
      this.vel += (-FLINCH_OMEGA * FLINCH_OMEGA * this.rollDeg - 2 * FLINCH_ZETA * FLINCH_OMEGA * this.vel) * h;
      this.rollDeg += this.vel * h;
      left -= h;
    }
    if (this.rollDeg > FLINCH_MAX_ROLL_DEG) this.rollDeg = FLINCH_MAX_ROLL_DEG;
    else if (this.rollDeg < -FLINCH_MAX_ROLL_DEG) this.rollDeg = -FLINCH_MAX_ROLL_DEG;
    if (Math.abs(this.rollDeg) < 1e-4 && Math.abs(this.vel) < 1e-3) {
      this.rollDeg = 0;
      this.vel = 0;
    }
  }

  reset(): void {
    this.rollDeg = 0;
    this.vel = 0;
    this.flip = 1;
  }
}
